import * as Sentry from "@sentry/react";
import { NavMenu } from "@shopify/app-bridge-react";
import "@shopify/polaris/build/esm/styles.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Echo from "laravel-echo";
import Pusher from 'pusher-js';
import { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { Provider as ReduxProvider } from "react-redux";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import AppSetupProvider from "./src/AppSetupProvider.js";
import i18n from "./src/locales/i18n";
import store from "./src/redux/store/store";
import WithPlanCheck from "./WithPlanCheck.js";

// Initialize Laravel Echo
// window.Pusher = Pusher;
// window.Echo = new Echo({
//     broadcaster: 'pusher',
//     key: import.meta.env.MIX_PUSHER_APP_KEY,
//     cluster: import.meta.env.MIX_PUSHER_APP_CLUSTER,
//     forceTLS: true
// });

const EmailEditor = lazy(() => import("./src/Dashboard/Settings/EmailEditor/index"));
const ReviewSettings = lazy(() => import("./src/Dashboard/Settings/ReviewSettings.js"));
const ShopReviewSettings = lazy(() => import("./src/Dashboard/Settings/ShopReviewSettings.js"));
const ReviewWidget = lazy(() => import("./src/Dashboard/Settings/ReviewWidget.js"));
const ReferralWidget = lazy(() => import("./src/Dashboard/Settings/ReferralWidget.js"));
const OtherWidget = lazy(() => import("./src/Dashboard/Settings/OtherWidget.js"));
const EmailSettings = lazy(() => import("./src/Dashboard/Settings/EmailSettings.js"));
const CustomDomain = lazy(() => import("./src/Dashboard/Settings/CustomDomain.js"));

const AppProvider = lazy(() =>
    import("@shopify/polaris").then((module) => ({
        default: module.AppProvider,
    }))
);

const translations = lazy(() =>
    import("@shopify/polaris/locales/en.json").then((module) => ({
        default: module,
    }))
);
const React = lazy(() => import("react"));
const SelectReview = lazy(() => import("./src/Dashboard/Import/SelectReviews"));
const Reviews = lazy(() => import("./src/Dashboard/Reviews/Reviews"));
const Settings = lazy(() => import("./src/Dashboard/Settings/Settings"));
const NewOnboarding = lazy(() =>
    import("./src/Dashboard/Partials/NewOnboarding")
);
const Home = lazy(() => import("./src/Dashboard/home"));
const HelpDocsAndVideos = lazy(() =>
    import("./src/Dashboard/HelpDocsAndVideos")
);
const UpgradePlanNew = lazy(() => import("./src/Dashboard/UpgradePlanNew"));
const Feedback = lazy(() => import("./src/Dashboard/Feedback"));
const ImportReviews = lazy(() => import("./src/Dashboard/Import"));
const Integrations = lazy(() =>
    import("./src/Dashboard/Integrations/Integrations")
);
const ProductGroup = lazy(() =>
    import("./src/Dashboard/ProductGroups/ProductGroup.js")
);
const ProductGroupCreateEdit = lazy(() =>
    import("./src/Dashboard/ProductGroups/ProductGroupCreateEdit.js")
)

// Wrap components that need plan check
const ProtectedReviews = WithPlanCheck(Reviews);
const ProtectedImportReviews = WithPlanCheck(ImportReviews);
const ProtectedProductGroup = WithPlanCheck(ProductGroup);
const ProtectedProductGroupCreateEdit = WithPlanCheck(ProductGroupCreateEdit);
const ProtectedIntegrations = WithPlanCheck(Integrations);
const ProtectedSettings = WithPlanCheck(Settings);

Sentry.init({
    dsn: "https://a83fc45599f86724140c72fc21e10f2f@o4504695773200384.ingest.us.sentry.io/4507160125505536",
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

function App() {

    // const hostname = window.location.hostname;

    // useEffect(() => {
    //     if (
    //         hostname !== "localhost" &&
    //         hostname !== "127.0.0.1" &&
    //         hostname !== "reevo.test"
    //     ) {
    //         const loadAnalytics = async () => {
    //             let [response] = await AnalyticsBrowser.load({
    //                 writeKey: "Rf2Jrycc33S9qmkW",
    //             });
    //             setAnalytics(response);
    //         };
    //         loadAnalytics();
    //     }
    // }, []);

    // useEffect(() => {
    //     // Send a pageview event once Analytics is ready.
    //     if (analytics) {
    //         analytics.page();
    //         analytics.identify(config.shopOrigin, {
    //             email: config.email,
    //         });
    //     }
    // }, [analytics]);

    // if (
    //     hostname === "localhost" ||
    //     hostname === "127.0.0.1" ||
    //     hostname === "reevo.test"
    // ) {
    //     new PerformanceObserver((list) => {
    //         for (const entry of list.getEntries()) {
    //             if (!entry.hadRecentInput) {
    //                 if (entry.sources) {
    //                     entry.sources.forEach((source) => {
    //                         if (source.node) {
    //                             source.node.style.outline = "2px solid red";
    //                             // sendSlackMessageToChannel(
    //                             //     "layout shift",
    //                             //     source.node
    //                             // );
    //                         }
    //                     });
    //                 }
    //             }
    //         }
    //     }).observe({ type: "layout-shift", buffered: true });

    //     new PerformanceObserver((list) => {
    //         for (const entry of list.getEntries()) {
    //             if (entry.entryType === "largest-contentful-paint") {
    //                 if (entry.startTime >= 2500) {
    //                     console.log("LCP over 2.5s:", entry);
    //                     if (entry.element) {
    //                         entry.element.style.border = "5px solid blue";
    //                     }
    //                 }else{
    //                     console.log("LCP less than 2.5s:", entry);
    //                 }
    //             }
    //         }
    //     }).observe({
    //         type: "largest-contentful-paint",
    //         buffered: true,
    //     });
    // }

    return (
        <ReduxProvider store={store}>
            <AppProvider i18n={translations}>
                <I18nextProvider i18n={i18n}>
                    <Router>
                        <Suspense fallback={<div>Loading...</div>}>
                            <AppSetupProvider>
                                <>
                                    <NavMenu>
                                        <a href="/app" rel="home">Home</a>
                                        <a href="/app/onboarding">Onboarding</a>
                                        <a href="/app/reviews-page">Reviews</a>
                                        <a href="/app/import-reviews">Import Reviews</a>
                                        <a href="/app/product-groups">Product Groups</a>
                                        <a href="/app/integrations">Integrations</a>
                                        <a href="/app/settings-page">Settings</a>
                                        <a href="/app/upgrade-plan">Billing</a>
                                        <a href="/app/help-docs-and-videos">Help Docs & Videos</a>
                                        <a href="/app/request-a-feature-page">Request a feature</a>
                                    </NavMenu>

                                    <Routes>
                                        <Route index element={<Home />} />
                                        <Route
                                            path="/app"
                                            element={<Home />}
                                        />
                                        <Route
                                            path="/"
                                            element={<Home />}
                                        />
                                        <Route
                                            path="/app/onboarding"
                                            element={<NewOnboarding />}
                                        />
                                        <Route
                                            path="/app/reviews-page"
                                            element={<ProtectedReviews />}
                                        />
                                        <Route
                                            path="/app/import-reviews/*"
                                            element={<ProtectedImportReviews />}
                                        />
                                        <Route
                                            path="app/product-groups"
                                            element={<ProtectedProductGroup />}
                                        />
                                        <Route
                                            path="/app/product-groups/product-group-create"
                                            element={<ProtectedProductGroupCreateEdit />}
                                        />
                                        <Route
                                            path="/app/integrations"
                                            element={<ProtectedIntegrations />}
                                        />
                                        <Route
                                            path="/app/settings-page"
                                            element={<ProtectedSettings />}
                                        />
                                        <Route
                                            path="/app/request-a-feature-page"
                                            element={<Feedback />}
                                        />
                                        <Route
                                            path="/app/upgrade-plan"
                                            element={<UpgradePlanNew />}
                                        />
                                        <Route
                                            path="/app/contact"
                                            element={<Home />}
                                        />
                                        <Route
                                            path="/app/select-review"
                                            element={<SelectReview />}
                                        />
                                        <Route
                                            path="/app/help-docs-and-videos"
                                            element={<HelpDocsAndVideos />}
                                        />
                                        <Route
                                            path="/app/settings-page/review-settings"
                                            element={<ReviewSettings />}
                                        />
                                        <Route
                                            path="/app/settings-page/shop-review-settings"
                                            element={<ShopReviewSettings />}
                                        />
                                        <Route
                                            path="/app/settings-page/review-widget-settings"
                                            element={<ReviewWidget />}
                                        />
                                        <Route
                                            path="/app/settings-page/referral-widget-settings"
                                            element={<ReferralWidget />}
                                        />
                                        <Route
                                            path="/app/settings-page/other-widgets"
                                            element={<OtherWidget />}
                                        />
                                        <Route
                                            path="/app/settings-page/email-settings"
                                            element={<EmailSettings />}
                                        />
                                        <Route
                                            path="/app/settings-page/custom-domain"
                                            element={<CustomDomain />}
                                        />
                                        <Route
                                            path="/app/settings-page/email-editor"
                                            element={<EmailEditor />}
                                        />
                                    </Routes>
                                </>
                            </AppSetupProvider>
                        </Suspense>
                    </Router>
                </I18nextProvider>
            </AppProvider >
        </ReduxProvider >
    );
}

export default App;

let appElement = document.getElementById("app");
if (appElement) {
    const root = ReactDOM.createRoot(appElement);
    root.render(<App {...appElement.dataset} />);
}
